import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { SearchIcon, FilterIcon, MapPinIcon, UsersIcon, ListIcon, MapIcon, CompassIcon, ChevronRightIcon, ClockIcon, PlusIcon, BellIcon, TrendingUpIcon, MessageCircleIcon, AnchorIcon, CalendarIcon, AwardIcon, WavesIcon, StarIcon, UserPlusIcon, ShareIcon } from 'lucide-react';
import { LocationStory } from '../components/community/LocationStory';
import { LocationPost } from '../components/community/LocationPost';
import { LocationGroup } from '../components/community/LocationGroup';
import { MapView } from '../components/community/MapView';
export const Community: React.FC = () => {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [activeTab, setActiveTab] = useState<'all' | 'nearby' | 'popular' | 'buddies' | 'events'>('all');
  const [contentFilter, setContentFilter] = useState<'all' | 'posts' | 'events' | 'photos'>('all');
  // Mock current location
  const currentLocation = {
    lat: -16.7834,
    lng: 145.6722
  };
  // Sample location stories
  const locationStories = [{
    name: 'Great Barrier Reef',
    image: 'https://images.unsplash.com/photo-1682687982167-d7fb3ed8541d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '5 km',
    isHot: true
  }, {
    name: 'Blue Corner',
    image: 'https://images.unsplash.com/photo-1544551763-92ab472cad5d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '1,200 km'
  }, {
    name: 'Barracuda Point',
    image: 'https://images.unsplash.com/photo-1682687982101-a37303708c7c?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '3,400 km'
  }, {
    name: 'SS Yongala',
    image: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '350 km',
    isHot: true
  }, {
    name: 'Osprey Reef',
    image: 'https://images.unsplash.com/photo-1570741066052-817c6de995c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '25 km'
  }, {
    name: 'Cod Hole',
    image: 'https://images.unsplash.com/photo-1560275619-4cc5fa59d3ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    distance: '18 km'
  }];
  // Sample posts data
  const posts = [{
    id: '1',
    user: {
      name: 'Sarah Thompson',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
    },
    location: {
      name: 'Great Barrier Reef',
      distance: '5 km',
      coordinates: {
        lat: -16.7555,
        lng: 145.6584
      },
      rating: 4.8,
      conditions: 'Excellent'
    },
    content: 'Just completed my 100th dive at the Great Barrier Reef! The visibility was amazing and we spotted several reef sharks and a sea turtle. Here are some photos from the dive!',
    images: ['https://images.unsplash.com/photo-1682687982167-d7fb3ed8541d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1544551763-92ab472cad5d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'],
    likes: 42,
    comments: 8,
    shares: 5,
    time: '2 hours ago',
    diveDetails: {
      depth: '18m',
      duration: '45 min',
      temperature: '24°C'
    }
  }, {
    id: '2',
    user: {
      name: 'Michael Chen',
      image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      role: 'Instructor'
    },
    location: {
      name: 'Sydney Harbour',
      distance: '1,500 km',
      coordinates: {
        lat: -33.8568,
        lng: 151.2153
      },
      rating: 4.2,
      conditions: 'Good'
    },
    content: "I'm organizing a night diving workshop in Sydney Harbour next week. Perfect for divers who want to experience the underwater world after dark. Limited spots available!",
    likes: 18,
    comments: 5,
    shares: 12,
    time: '5 hours ago',
    isEvent: true,
    event: {
      title: 'Night Diving Workshop',
      type: 'Workshop',
      date: 'July 22, 2023',
      time: '7:30 PM',
      going: 12
    }
  }, {
    id: '3',
    user: {
      name: 'Emma Wilson',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
    },
    location: {
      name: 'Osprey Reef',
      distance: '25 km',
      coordinates: {
        lat: -16.4333,
        lng: 146.0
      },
      rating: 4.9,
      conditions: 'Excellent'
    },
    content: 'Osprey Reef never disappoints! Today we saw a group of hammerhead sharks circling the reef wall. The visibility was over 30 meters!',
    images: ['https://images.unsplash.com/photo-1570741066052-817c6de995c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80', 'https://images.unsplash.com/photo-1544552866-d3ed42536d26?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'],
    likes: 36,
    comments: 14,
    shares: 8,
    time: 'Yesterday',
    diveDetails: {
      depth: '25m',
      duration: '52 min',
      temperature: '26°C'
    }
  }];
  // Location groups
  const locationGroups = [{
    location: {
      name: 'Great Barrier Reef',
      distance: '5 km',
      postCount: 24,
      rating: 4.8
    },
    posts: [posts[0], {
      id: '4',
      user: {
        name: 'David Jones',
        image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
      },
      location: {
        name: 'Great Barrier Reef',
        distance: '5 km',
        coordinates: {
          lat: -16.7555,
          lng: 145.6584
        },
        rating: 4.8,
        conditions: 'Excellent'
      },
      content: 'Found Nemo! 🐠 Amazing visibility today at the reef.',
      images: ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'],
      likes: 28,
      comments: 6,
      shares: 3,
      time: '3 days ago',
      diveDetails: {
        depth: '15m',
        duration: '40 min',
        temperature: '25°C'
      }
    }]
  }, {
    location: {
      name: 'Osprey Reef',
      distance: '25 km',
      postCount: 12,
      rating: 4.9
    },
    posts: [posts[2]]
  }];
  // Map locations
  const mapLocations = [{
    name: 'Great Barrier Reef',
    coordinates: {
      lat: -16.7555,
      lng: 145.6584
    },
    postCount: 24
  }, {
    name: 'Osprey Reef',
    coordinates: {
      lat: -16.4333,
      lng: 146.0
    },
    postCount: 12
  }, {
    name: 'Cod Hole',
    coordinates: {
      lat: -16.7555,
      lng: 145.6584
    },
    postCount: 8
  }, {
    name: 'SS Yongala',
    coordinates: {
      lat: -19.3055,
      lng: 147.623
    },
    postCount: 15
  }, {
    name: 'Sydney Harbour',
    coordinates: {
      lat: -33.8568,
      lng: 151.2153
    },
    postCount: 5
  }];
  // Suggested dive buddies
  const suggestedBuddies = [{
    id: '1',
    name: 'Alex Morgan',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    certification: 'Advanced Open Water',
    mutual: 3
  }, {
    id: '2',
    name: 'Jamie Chen',
    image: 'https://images.unsplash.com/photo-1499952127939-9bbf5af6c51c?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    certification: 'Rescue Diver',
    mutual: 5
  }, {
    id: '3',
    name: 'Taylor Reed',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    certification: 'Divemaster',
    mutual: 2
  }];
  // Trending dive sites
  const trendingDiveSites = [{
    id: '1',
    name: 'Ribbon Reefs',
    image: 'https://images.unsplash.com/photo-1546026423-cc4642628d2b?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    rating: 4.7,
    posts: 18
  }, {
    id: '2',
    name: 'Shark Point',
    image: 'https://images.unsplash.com/photo-1534766438357-2b270dbd1b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
    rating: 4.5,
    posts: 24
  }];
  // Activity feed
  const activityFeed = [{
    id: '1',
    type: 'post',
    user: 'Sarah Thompson',
    action: 'posted about',
    target: 'Great Barrier Reef',
    time: '2 hours ago'
  }, {
    id: '2',
    type: 'event',
    user: 'Michael Chen',
    action: 'created an event',
    target: 'Night Diving Workshop',
    time: '5 hours ago'
  }, {
    id: '3',
    type: 'certification',
    user: 'Jamie Lee',
    action: 'earned',
    target: 'Advanced Open Water certification',
    time: 'Yesterday'
  }, {
    id: '4',
    type: 'dive',
    user: 'Alex Morgan',
    action: 'logged a dive at',
    target: 'SS Yongala',
    time: '2 days ago'
  }];
  return <div className="relative" data-id="element-429">
      {/* Search bar with improved styling */}
      <div className="relative mb-5 flex items-center" data-id="element-430">
        <div className="relative flex-1 flex items-center" data-id="element-431">
          <div className="relative flex-1" data-id="element-432">
            <input type="text" placeholder="Search dive sites, buddies, or posts..." className="w-full p-3 pl-10 pr-[90px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary shadow-sm" data-id="element-433" />
            <SearchIcon size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-434" />
          </div>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex border rounded-lg overflow-hidden shadow-sm" data-id="element-435">
            <button className={`px-3 py-1.5 transition-colors ${view === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} onClick={() => setView('list')} aria-label="List view" data-id="element-436">
              <ListIcon size={18} data-id="element-437" />
            </button>
            <button className={`px-3 py-1.5 transition-colors ${view === 'map' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} onClick={() => setView('map')} aria-label="Map view" data-id="element-438">
              <MapIcon size={18} data-id="element-439" />
            </button>
          </div>
        </div>
        <div className="flex items-center ml-3 gap-2" data-id="element-440">
          <Button variant="outline" size="sm" className="relative h-9 w-9 p-0 rounded-full shadow-sm hover:bg-gray-50 border-gray-200" aria-label="Notifications" data-id="element-441">
            <BellIcon size={16} data-id="element-442" />
            <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center shadow-sm" data-id="element-443">
              3
            </span>
          </Button>
          <Button variant="primary" size="sm" className="h-9 w-9 p-0 rounded-full shadow-sm hover:shadow" aria-label="Find Buddies" data-id="element-444">
            <UsersIcon size={16} data-id="element-445" />
          </Button>
        </div>
      </div>
      {/* Main tabs - enhanced with consistent styling */}
      <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar mb-4" data-id="element-446">
        <Button variant={activeTab === 'all' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('all')} className={`px-4 transition-all ${activeTab === 'all' ? 'shadow-sm font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-447">
          All
        </Button>
        <Button variant={activeTab === 'nearby' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('nearby')} className={`px-4 transition-all ${activeTab === 'nearby' ? 'shadow-sm font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-448">
          <CompassIcon size={14} className="mr-1.5" data-id="element-449" />
          Near Me
        </Button>
        <Button variant={activeTab === 'popular' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('popular')} className={`px-4 transition-all ${activeTab === 'popular' ? 'shadow-sm font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-450">
          <TrendingUpIcon size={14} className="mr-1.5" data-id="element-451" />
          Popular
        </Button>
        <Button variant={activeTab === 'buddies' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('buddies')} className={`px-4 transition-all ${activeTab === 'buddies' ? 'shadow-sm font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-452">
          <UsersIcon size={14} className="mr-1.5" data-id="element-453" />
          Buddies
        </Button>
        <Button variant={activeTab === 'events' ? 'primary' : 'outline'} size="sm" onClick={() => setActiveTab('events')} className={`px-4 transition-all ${activeTab === 'events' ? 'shadow-sm font-medium' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-454">
          <CalendarIcon size={14} className="mr-1.5" data-id="element-455" />
          Events
        </Button>
      </div>
      {/* Content type filters - improved visual consistency */}
      <div className="flex flex-wrap gap-2 mb-6 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-456">
        <Button variant={contentFilter === 'all' ? 'secondary' : 'ghost'} size="sm" onClick={() => setContentFilter('all')} className={`text-xs py-1 px-3 transition-colors ${contentFilter === 'all' ? 'shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-457">
          All Content
        </Button>
        <Button variant={contentFilter === 'posts' ? 'secondary' : 'ghost'} size="sm" onClick={() => setContentFilter('posts')} className={`text-xs py-1 px-3 transition-colors ${contentFilter === 'posts' ? 'shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-458">
          Posts
        </Button>
        <Button variant={contentFilter === 'events' ? 'secondary' : 'ghost'} size="sm" onClick={() => setContentFilter('events')} className={`text-xs py-1 px-3 transition-colors ${contentFilter === 'events' ? 'shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-459">
          Events
        </Button>
        <Button variant={contentFilter === 'photos' ? 'secondary' : 'ghost'} size="sm" onClick={() => setContentFilter('photos')} className={`text-xs py-1 px-3 transition-colors ${contentFilter === 'photos' ? 'shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`} data-id="element-460">
          Photos
        </Button>
        <Button variant="outline" size="sm" className="text-xs py-1 px-2 ml-auto bg-white hover:bg-gray-50 h-7 w-7" aria-label="Filter" data-id="element-461">
          <FilterIcon size={14} data-id="element-462" />
        </Button>
      </div>
      {view === 'list' ? <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-463">
          {/* Main content column */}
          <div className="md:col-span-2 space-y-5" data-id="element-464">
            {/* Location Stories - Enhanced with visual indicators */}
            <div className="mb-5" data-id="element-465">
              <div className="flex items-center justify-between mb-3" data-id="element-466">
                <h2 className="font-semibold flex items-center" data-id="element-467">
                  <MapPinIcon size={16} className="mr-2 text-primary" data-id="element-468" />
                  Popular Dive Locations
                </h2>
                <Button variant="ghost" size="sm" icon={<ChevronRightIcon size={16} data-id="element-470" />} className="text-sm" data-id="element-469">
                  View All
                </Button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar" data-id="element-471">
                {locationStories.map((location, index) => <LocationStory key={index} name={location.name} image={location.image} distance={location.distance} isHot={location.isHot} data-id="element-472" />)}
              </div>
            </div>
            {/* Content */}
            {activeTab === 'all' && <div className="space-y-5" data-id="element-473">
                {/* Location groups */}
                {locationGroups.map((group, index) => <LocationGroup key={index} location={group.location} posts={group.posts} data-id="element-474" />)}
                {/* Individual posts */}
                <LocationPost {...posts[1]} data-id="element-475" />
              </div>}
            {activeTab === 'nearby' && <div className="space-y-5" data-id="element-476">
                <Card className="p-0 overflow-hidden" data-id="element-477">
                  <div className="p-4 bg-primary/10 border-b border-primary/20" data-id="element-478">
                    <div className="flex items-center justify-between" data-id="element-479">
                      <div className="flex items-center" data-id="element-480">
                        <CompassIcon size={18} className="text-primary mr-2" data-id="element-481" />
                        <h2 className="font-semibold" data-id="element-482">Locations Near You</h2>
                      </div>
                      <Badge variant="primary" className="text-xs" data-id="element-483">
                        Within 30 km
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-484">
                    <div className="space-y-4" data-id="element-485">
                      {locationGroups.filter(g => parseInt(g.location.distance) < 30).map((group, index) => <div key={index} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100" data-id="element-486">
                            <div className="flex items-center" data-id="element-487">
                              <div className="w-12 h-12 rounded-lg overflow-hidden mr-3" data-id="element-488">
                                <img src={`https://images.unsplash.com/photo-1544551763-${index % 2 === 0 ? '92ab472cad5d' : '46a013bb70d5'}?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80`} alt={group.location.name} className="w-full h-full object-cover" data-id="element-489" />
                              </div>
                              <div data-id="element-490">
                                <h3 className="font-medium" data-id="element-491">
                                  {group.location.name}
                                </h3>
                                <div className="flex items-center text-sm text-gray-500" data-id="element-492">
                                  <MapPinIcon size={12} className="mr-1" data-id="element-493" />
                                  <span data-id="element-494">{group.location.distance} away</span>
                                </div>
                                <div className="flex items-center mt-1" data-id="element-495">
                                  {[...Array(5)].map((_, i) => <StarIcon key={i} size={12} className={i < Math.floor(group.location.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} data-id="element-496" />)}
                                  <span className="text-xs ml-1" data-id="element-497">
                                    {group.location.rating}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right" data-id="element-498">
                              <div className="text-sm text-gray-500" data-id="element-499">
                                {group.location.postCount} posts
                              </div>
                              <Button variant="primary" size="sm" className="mt-1" data-id="element-500">
                                View Details
                              </Button>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </Card>
                {/* Posts from nearby locations */}
                {posts.filter(p => parseInt(p.location.distance) < 30).map((post, index) => <LocationPost key={index} {...post} data-id="element-501" />)}
              </div>}
            {activeTab === 'events' && <div className="space-y-5" data-id="element-502">
                <Card className="mb-5" data-id="element-503">
                  <h2 className="font-semibold mb-4" data-id="element-504">Upcoming Events</h2>
                  <div className="space-y-4" data-id="element-505">
                    <div className="border-b pb-3" data-id="element-506">
                      <div className="flex justify-between" data-id="element-507">
                        <h3 className="font-medium" data-id="element-508">
                          Great Barrier Reef Cleanup
                        </h3>
                        <Badge variant="accent" data-id="element-509">Conservation</Badge>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-510">
                        <MapPinIcon size={14} className="mr-1" data-id="element-511" />
                        Cairns, Australia • 5 km away
                      </p>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-512">
                        July 15, 2023 • 9:00 AM
                      </p>
                      <div className="flex items-center justify-between mt-2" data-id="element-513">
                        <div className="flex items-center gap-2" data-id="element-514">
                          <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-515" />
                          <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-516" />
                          <img src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-517" />
                          <span className="text-xs text-gray-500" data-id="element-518">
                            +24 going
                          </span>
                        </div>
                        <div className="flex gap-2" data-id="element-519">
                          <Button variant="outline" size="sm" icon={<ShareIcon size={14} data-id="element-521" />} data-id="element-520">
                            Share
                          </Button>
                          <Button variant="primary" size="sm" data-id="element-522">
                            RSVP
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-523">
                      <div className="flex justify-between" data-id="element-524">
                        <h3 className="font-medium" data-id="element-525">Night Diving Workshop</h3>
                        <Badge variant="primary" data-id="element-526">Workshop</Badge>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-527">
                        <MapPinIcon size={14} className="mr-1" data-id="element-528" />
                        Sydney Harbour, Australia • 1,500 km away
                      </p>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-529">
                        July 22, 2023 • 7:30 PM
                      </p>
                      <div className="flex items-center justify-between mt-2" data-id="element-530">
                        <div className="flex items-center gap-2" data-id="element-531">
                          <img src="https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-532" />
                          <img src="https://images.unsplash.com/photo-1499952127939-9bbf5af6c51c?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-533" />
                          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=40&q=80" alt="Participant" className="w-6 h-6 rounded-full" data-id="element-534" />
                          <span className="text-xs text-gray-500" data-id="element-535">
                            +12 going
                          </span>
                        </div>
                        <div className="flex gap-2" data-id="element-536">
                          <Button variant="outline" size="sm" icon={<ShareIcon size={14} data-id="element-538" />} data-id="element-537">
                            Share
                          </Button>
                          <Button variant="primary" size="sm" data-id="element-539">
                            RSVP
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" fullWidth className="mt-4" data-id="element-540">
                    View All Events
                  </Button>
                </Card>
                {/* Event posts */}
                {posts.filter(p => p.isEvent).map((post, index) => <LocationPost key={index} {...post} data-id="element-541" />)}
              </div>}
          </div>
          {/* Sidebar column - New addition */}
          <div className="space-y-5 hidden md:block" data-id="element-542">
            {/* Suggested Dive Buddies */}
            <Card data-id="element-543">
              <div className="flex items-center justify-between mb-3" data-id="element-544">
                <h2 className="font-semibold flex items-center" data-id="element-545">
                  <UsersIcon size={16} className="mr-2 text-primary" data-id="element-546" />
                  Suggested Buddies
                </h2>
                <Button variant="ghost" size="sm" className="text-primary text-sm" data-id="element-547">
                  See All
                </Button>
              </div>
              <div className="space-y-3" data-id="element-548">
                {suggestedBuddies.map(buddy => <div key={buddy.id} className="flex items-center justify-between border-b border-gray-100 pb-3" data-id="element-549">
                    <div className="flex items-center gap-3" data-id="element-550">
                      <img src={buddy.image} alt={buddy.name} className="w-10 h-10 rounded-full" data-id="element-551" />
                      <div data-id="element-552">
                        <h3 className="font-medium" data-id="element-553">{buddy.name}</h3>
                        <div className="flex items-center" data-id="element-554">
                          <Badge variant="secondary" className="text-xs mr-2" data-id="element-555">
                            {buddy.certification}
                          </Badge>
                          <span className="text-xs text-gray-500" data-id="element-556">
                            {buddy.mutual} mutual friends
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button variant="primary" size="sm" icon={<UserPlusIcon size={14} data-id="element-558" />} data-id="element-557">
                      Connect
                    </Button>
                  </div>)}
              </div>
              <Button variant="outline" fullWidth className="mt-3" data-id="element-559">
                Find More Buddies
              </Button>
            </Card>
            {/* Trending Dive Sites */}
            <Card data-id="element-560">
              <div className="flex items-center justify-between mb-3" data-id="element-561">
                <h2 className="font-semibold flex items-center" data-id="element-562">
                  <TrendingUpIcon size={16} className="mr-2 text-primary" data-id="element-563" />
                  Trending Dive Sites
                </h2>
                <Button variant="ghost" size="sm" className="text-primary text-sm" data-id="element-564">
                  See All
                </Button>
              </div>
              <div className="space-y-3" data-id="element-565">
                {trendingDiveSites.map(site => <div key={site.id} className="flex gap-3 border-b border-gray-100 pb-3" data-id="element-566">
                    <div className="w-16 h-16 rounded-lg overflow-hidden" data-id="element-567">
                      <img src={site.image} alt={site.name} className="w-full h-full object-cover" data-id="element-568" />
                    </div>
                    <div className="flex-1" data-id="element-569">
                      <h3 className="font-medium" data-id="element-570">{site.name}</h3>
                      <div className="flex items-center mt-1" data-id="element-571">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} size={12} className={i < Math.floor(site.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} data-id="element-572" />)}
                        <span className="text-xs ml-1" data-id="element-573">{site.rating}</span>
                      </div>
                      <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-574">
                        <MessageCircleIcon size={12} className="mr-1" data-id="element-575" />
                        <span data-id="element-576">{site.posts} new posts this week</span>
                      </div>
                    </div>
                  </div>)}
              </div>
              <Button variant="outline" fullWidth className="mt-3" data-id="element-577">
                Explore More Sites
              </Button>
            </Card>
            {/* Dive Conditions Card */}
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20" data-id="element-578">
              <div className="flex items-center justify-between mb-3" data-id="element-579">
                <h2 className="font-semibold flex items-center" data-id="element-580">
                  <WavesIcon size={16} className="mr-2 text-primary" data-id="element-581" />
                  Today's Dive Conditions
                </h2>
              </div>
              <div className="bg-white rounded-lg p-3 mb-2" data-id="element-582">
                <div className="flex justify-between items-center" data-id="element-583">
                  <div data-id="element-584">
                    <h3 className="font-medium" data-id="element-585">Great Barrier Reef</h3>
                    <p className="text-xs text-gray-500" data-id="element-586">5 km away</p>
                  </div>
                  <Badge variant="success" data-id="element-587">Excellent</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-center" data-id="element-588">
                  <div className="bg-gray-50 rounded p-1" data-id="element-589">
                    <p className="text-xs text-gray-500" data-id="element-590">Visibility</p>
                    <p className="font-medium" data-id="element-591">25m</p>
                  </div>
                  <div className="bg-gray-50 rounded p-1" data-id="element-592">
                    <p className="text-xs text-gray-500" data-id="element-593">Temperature</p>
                    <p className="font-medium" data-id="element-594">26°C</p>
                  </div>
                  <div className="bg-gray-50 rounded p-1" data-id="element-595">
                    <p className="text-xs text-gray-500" data-id="element-596">Current</p>
                    <p className="font-medium" data-id="element-597">Mild</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3" data-id="element-598">
                <div className="flex justify-between items-center" data-id="element-599">
                  <div data-id="element-600">
                    <h3 className="font-medium" data-id="element-601">Osprey Reef</h3>
                    <p className="text-xs text-gray-500" data-id="element-602">25 km away</p>
                  </div>
                  <Badge variant="success" data-id="element-603">Excellent</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-center" data-id="element-604">
                  <div className="bg-gray-50 rounded p-1" data-id="element-605">
                    <p className="text-xs text-gray-500" data-id="element-606">Visibility</p>
                    <p className="font-medium" data-id="element-607">30m</p>
                  </div>
                  <div className="bg-gray-50 rounded p-1" data-id="element-608">
                    <p className="text-xs text-gray-500" data-id="element-609">Temperature</p>
                    <p className="font-medium" data-id="element-610">25°C</p>
                  </div>
                  <div className="bg-gray-50 rounded p-1" data-id="element-611">
                    <p className="text-xs text-gray-500" data-id="element-612">Current</p>
                    <p className="font-medium" data-id="element-613">Moderate</p>
                  </div>
                </div>
              </div>
              <Button variant="primary" fullWidth className="mt-3" data-id="element-614">
                Check More Locations
              </Button>
            </Card>
          </div>
        </div> : <MapView locations={mapLocations} currentLocation={currentLocation} data-id="element-615" />}
      {/* Floating Action Button for creating a post */}
      <Link to="/community/create-post" className="fixed bottom-20 right-5 z-20 md:bottom-5" data-id="element-616">
        <Button variant="primary" className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center" aria-label="Create a post" data-id="element-617">
          <PlusIcon size={24} data-id="element-618" />
        </Button>
      </Link>
    </div>;
};